// External Imports
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { SubmissionError, submit, change } from 'redux-form';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import throttle from 'lodash/throttle';

// PPUI Imports
import {
  Container,
  Col,
  Row,
  Button,
  ArrowLeftIcon,
  Toast,
  IconButton,
} from '@paypalcorp/pp-react';
import { ProgressMeter } from '@paypalcorp/pp-react-progress-meter';

// Internal Imports
import WizardStep1Form from './WizardStepsV3/WizardStep1';
import WizardStep2Form from './WizardStepsV3/WizardStep2';
import WizardStep3Form from './WizardStepsV3/WizardStep3';
import WizardStep4Form from './WizardStepsV3/WizardStep4';
import ExtendSession from './ExtendSession';
import { isLoggedInUser } from '../utils/authUtils';
import { hideSpinner, showSpinner } from '../utils/spinnerUtils';
import ButtonFactoryLogin from './login/ButtonFactoryLogin';
import PreviewNavigator from './preview/PreviewNavigator';
import * as ButtonFactoryActions from '../actions/actionsButtonFactory';
import { getParameterByName } from '../../utility/queryStringHelper';
import {
  getPageUrl,
  getStepUrl,
  isDonateButton,
  isHostedFlow,
  isUnhostedFlow,
  getWizardTotalSteps,
  getFlowType,
  isDonateLink,
  handleLoginHelper,
  handleSignupHelper,
} from '../utils/buttonFactoryUtils';
import { trackWizardDetailsPage } from '../utils/ButtonFactoryFPT';
import {
  getButtonVariable,
  mapServerFieldToClientField,
  shouldUsePayPalButtonStyle,
} from '../lib/buttonModelV3';
import getLocaleMessages from '../utils/localesMessages';
import DonateButtonFeedback from './DonateButtonFeedback';
import WizardMobilePreview from './preview/WizardMobilePreview';
import { BTN_FACTORY_V3_FORM, MAX_MOBILE_WIDTH } from '../clientConstants';
import WizardStep1Preview from './preview/WizardStep1Preview';
import { getOptionsForLanguageField } from '../utils/buttonFactoryUtils';
import { ACTION, FPTI_SPEC, SCREEN } from '../utilsV3/fptiConstants';
import {
  trackEventWithData,
  trackEventWithGlobalData,
} from '../utilsV3/analytics';
import CountryNotSupportedTile from './modals/CountryNotSupportedTile';
import BasicModal from '../componentsV3/modals/BasicModal';
import {
  LOGO_TARGET_BUTTON,
  LOGO_TARGET_PROFILE,
  LOGO_TARGET_NOLOGO,
} from '../reducers/reducerButtonFactoryV3';

const { actionable, quitModal, unactionable } =
  getLocaleMessages('buttonsV3/errors');
const {
  continueButton,
  finishButton,
  previewButton,
  previewDonatePage,
  previewTitle,
  backFundraisers,
  toast: { duplicatePage } = {},
} = getLocaleMessages('buttonsV3/common');
const { stepTitle, stepCount } = getLocaleMessages('buttonsV3/progressMeter');
const { previewContainerHeader } = getLocaleMessages('buttonsV3/buttonPreview');
const errors = getLocaleMessages('buttonsV3/fieldErrors');

const progressMeterTitles = isDonateButton()
  ? stepTitle.button
  : stepTitle.link;

// Workaround for issue in content fro link flow step 2
// TODO: Remove this if once we get correct content fro progressMeter.properties
if (isDonateLink()) {
  progressMeterTitles[2] = stepTitle.button[3];
}

const ButtonFactoryWizardV3 = ({
  application = {},
  buttonFactoryV3 = {},
  destroyForm,
  dispatch,
  form: { buttonFactoryV3: { values: formValues = {} } = {} } = {},
  getButton,
  goToNextStep,
  goToPreviousStep,
  history,
  initializeWizard,
  location,
  match,
  saveButton,
  uploadImage,
  updateBusinessProfileLogo,
  updateBusinessImageUrls,
  updateFlowSpec,
  setCurrentStep,
  openMobilePreview,
  closeMobilePreview,
  updateIsMobile,
  optionsForLanguage,
}) => {
  const { busy, currentStep, totalSteps } = buttonFactoryV3;
  const [cancelBtnFptiSpec, setCancelBtnFptiSpec] = useState({});
  const [backBtnFptiSpec, setBackBtnFptiSpec] = useState({});
  const [continueBtnFptiSpec, setContinueBtnFptiSpec] = useState({});
  const [finishBtnFptiSpec, setFinishBtnFptiSpec] = useState({});
  const [backToManageFptiSpec, setBackToManageFptiSpec] = useState({});
  const [feedbackBtnFptiSpec, setFeedbackBtnFptiSpec] = useState({});
  const [previewTabFptiSpec, setPreviewTabFptiSpec] = useState({});
  const [screen, setScreen] = useState('');

  // Wizard step 1 Dropzone handle variables
  const [showButtonSpinner, setShowButtonSpinner] = useState(false);
  const [imageUpdated, setImageUpdated] = useState(false);
  const [dropzoneError, setDropzoneError] = useState('');

  const [logoClientError, setLogoClientError] = useState('');
  const [bannerClientError, setBannerClientError] = useState('');

  const isHosted = isHostedFlow();
  const isButtonFlow = isDonateButton();
  const isButtonConfigurePage = isButtonFlow && currentStep === 1,
    isExperienceSetupPage =
      (isButtonFlow && currentStep === 2) ||
      (!isButtonFlow && currentStep === 1),
    isSetAmountPage =
      (isButtonFlow && currentStep === 3) ||
      (!isButtonFlow && currentStep === 2),
    isAdvancedSettingsPage =
      (isButtonFlow && isHosted && currentStep === 4) ||
      (!isButtonFlow && currentStep === 3);

  const {
    amountType,
    bannerImageUrl,
    buttonImageUrl,
    buttonSize,
    cancelReturn,
    countryCode,
    coverFeesEnabled,
    extraVariablesView,
    language,
    listPrograms,
    noAdvancedVariables,
    noBannerImage,
    noCancelReturn,
    noLogoImage,
    noNote,
    noShipping,
    purpose,
    receiverCurrency,
    recurringEnabled,
    return: returnUrl,
    showAcceptedCardLogos,
    useFullPageExperience,
    usePaypalButtonStyle,
  } = formValues;
  const isDuplicating = !(match.path.indexOf('/duplicate') === -1);
  const isEditing = !(match.path.indexOf('/edit') === -1);

  const [isCountryNotSupported, setIsCountryNotSupported] = useState(false);
  const [shouldShowQuitModal, setShouldShowQuitModal] = useState(false);
  const [shouldShowActionableErrorModal, setShouldShowActionableErrorModal] =
    useState(false);
  const [errorCode, setErrorCode] = useState(null);
  const [errorDesc, setErrorDesc] = useState(null);
  const [
    shouldShowUnactionableErrorModal,
    setShouldShowUnactionableErrorModal,
  ] = useState(false);
  const [shouldShowDuplicateToast, setShouldShowDuplicateToast] =
    useState(false);

  const wizardContainer = document.getElementsByClassName(
    'wizard-step-container'
  );

  const initializeWizardComponent = (button) => {
    initializeWizard(button);

    if (currentStep !== 1) {
      const firstPageUrl = getStepUrl(location, currentStep, 1);
      history.replace(firstPageUrl);
    }
  };

  const handleUnhostedButton = () => {
    const { button = {} } = buttonFactoryV3;
    if (_isEmpty(button)) {
      window.location.href = `/donate/buttons${location.search}`;
    } else {
      initializeWizardComponent(button);
    }
  };

  const handleButtonPreviewError = (e) => {
    console.log('handleButtonPreviewError ', e);
  };

  const getLanguage = () => {
    if (language) {
      return language;
    }
    const languages = optionsForLanguage;
    // Return first language
    return _get(languages, '[0].value');
  };

  const checkDeviceType = () => {
    updateIsMobile(window.innerWidth < MAX_MOBILE_WIDTH, 200);
  };

  const setFptiSpecs = () => {
    if (isButtonConfigurePage) {
      setContinueBtnFptiSpec(FPTI_SPEC.CONFIGURE_BUTTON_PAGE_CONTINUE_CL);
      setBackToManageFptiSpec(FPTI_SPEC.CONFIGURE_BUTTON_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_FEEDBACK_CL);
      setScreen(SCREEN.CONFIGURE_BUTTON);
    } else if (isExperienceSetupPage) {
      setContinueBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_CONTINUE_CL);
      setBackBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_BACK_CL);
      setBackToManageFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_FEEDBACK_CL);
      setPreviewTabFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_PREVIEW_TAB_CL);
      setScreen(SCREEN.EXPERIENCE_SETUP);
    } else if (isSetAmountPage) {
      setContinueBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_CONTINUE_CL);
      setBackBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_BACK_CL);
      setBackToManageFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_FEEDBACK_CL);
      setPreviewTabFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_PREVIEW_TAB_CL);
      setScreen(SCREEN.SET_AMOUNT);
    } else if (isAdvancedSettingsPage) {
      setBackBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_BACK_CL);
      setBackToManageFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_NAV_BACK_CL);
      setFeedbackBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_FEEDBACK_CL);
      setPreviewTabFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_PREVIEW_TAB_CL);
      setScreen(SCREEN.ADVANCED_SETTINGS);
    }

    if (isButtonConfigurePage) {
      setCancelBtnFptiSpec(FPTI_SPEC.CONFIGURE_BUTTON_PAGE_CANCEL_CL);
    } else if (isExperienceSetupPage && !isButtonFlow) {
      setCancelBtnFptiSpec(FPTI_SPEC.EXPERIENCE_SETUP_PAGE_CANCEL_CL);
    }

    if (isAdvancedSettingsPage) {
      setFinishBtnFptiSpec(FPTI_SPEC.ADVANCED_SETTINGS_PAGE_FINISH_CL);
    } else if (isSetAmountPage && isUnhostedFlow) {
      setFinishBtnFptiSpec(FPTI_SPEC.SET_AMOUNT_PAGE_FINISH_CL);
    }
  };

  const handleUnsupportedCountries = () => {
    setIsCountryNotSupported(true);
    hideSpinner();
    return;
  };

  useEffect(() => {
    const buttonId = _get(match, 'params.buttonId');
    const isLoggedIn = isLoggedInUser(application);

    if (!isLoggedIn) {
      hideSpinner();
      return;
    } else if (isLoggedIn && application.isUnsupported) {
      handleUnsupportedCountries();
    }

    checkDeviceType();
    window.addEventListener('resize', throttle(checkDeviceType, 200));

    if (buttonId === 'unhosted') {
      handleUnhostedButton();
      return;
    }

    if (!_isEmpty(buttonId)) {
      showSpinner();
      getButton(buttonId)
        .then((response) => {
          if (!_isEmpty(response?.name)) {
            const code = response?.name;
            window.location.href = `/donate/buttons/manage?error=${code}`;
          } else {
            const flowType = getFlowType();
            updateFlowSpec({
              flowType,
              totalSteps: getWizardTotalSteps(flowType),
            });
            hideSpinner();
          }
        })
        .catch((error) => {
          showSpinner();
          const code = _get(error, 'responseJSON.button.name');
          window.location.href = `/donate/buttons/manage?error=${code}`;
        });
    } else {
      // If buttonId is not present, meaning that this is new button
      // creation and the edit/duplicate routes are excluded
      // We need to check if fromManage=true is available in the query
      // param, if not, redirect user to manage page
      const fromManage = getParameterByName('fromManage');
      if (!fromManage) {
        window.location.href = '/donate/buttons/manage';
      } else {
        initializeWizardComponent();
        const flowType = getFlowType();
        updateFlowSpec({
          flowType,
          totalSteps: getWizardTotalSteps(flowType),
        });
        hideSpinner();
      }
    }

    setCurrentStep(currentStep);

    if (isDuplicating) {
      setShouldShowDuplicateToast(true);
    }
  }, []);

  useEffect(() => {
    setFptiSpecs();
  }, [currentStep]);

  const handleLogin = () => {
    trackEventWithData(FPTI_SPEC.LANDING_PAGE_LOGIN_CL, { version: '3.0' });
    handleLoginHelper(history);
  };

  const handleSignUp = () => {
    trackEventWithData(FPTI_SPEC.LANDING_PAGE_SIGNUP_CL, { version: '3.0' });
    handleSignupHelper(history);
  };

  const handleBackClick = () => {
    const prevPageUrl = getStepUrl(location, currentStep, currentStep - 1);

    trackEventWithData(backBtnFptiSpec, { step: currentStep });

    goToPreviousStep();
    history.push(prevPageUrl);
  };

  const handleContinueClick = () => {
    const nextPageUrl = getStepUrl(location, currentStep, currentStep + 1);

    trackEventWithGlobalData(continueBtnFptiSpec, {
      ...(isButtonConfigurePage
        ? {
            button_size: buttonSize,
            button_type:
              usePaypalButtonStyle === 'yes' ? 'PAYPAL' : 'customized',
            ...(usePaypalButtonStyle === 'yes'
              ? { card_logo: showAcceptedCardLogos ? 'Yes' : 'No' }
              : {}),
            page_language: language,
            receiver_country: countryCode,
            step: currentStep,
          }
        : {}),
      ...(isExperienceSetupPage
        ? {
            display_type:
              useFullPageExperience === 'no' ? 'pop-up' : 'full page',
            logo: noLogoImage === 'yes' ? 'off' : 'on',
            img: noBannerImage === 'yes' ? 'off' : 'on',
            message: !_isEmpty(purpose) ? 'yes' : 'no',
            step: currentStep,
          }
        : {}),
      ...(isSetAmountPage
        ? {
            amount_type: amountType,
            choose_program: listPrograms === 'yes' ? 'on' : 'off',
            curr: receiverCurrency,
            fee_type: coverFeesEnabled === 'yes' ? 'offset' : 'none',
            is_recurring: recurringEnabled === 'yes' ? 'on' : 'off',
            step: currentStep,
          }
        : {}),
    });

    goToNextStep();
    history.push(nextPageUrl);
    if (buttonFactoryV3.isMobile) {
      window.scrollTo(0, 0);
    } else {
      wizardContainer.scrollTop = 0;
    }
  };

  const handleCancelClick = () => {
    trackEventWithData(cancelBtnFptiSpec, { step: currentStep });
    setShouldShowQuitModal(true);
  };

  const handleTopCancelClick = () => {
    trackEventWithData(backToManageFptiSpec, { step: currentStep });
    setShouldShowQuitModal(true);
  };

  const handleQuitClick = () => {
    showSpinner();
    trackEventWithData(FPTI_SPEC.ALERT_MODAL_CONFIRM_CL, {
      action: ACTION.QUIT,
      step: 1,
      link: 'quit',
    });
    window.location.pathname = '/donate/buttons/manage';
    wizardContainer.scrollTop = 0;
  };

  const closeQuitModal = () => {
    trackEventWithData(FPTI_SPEC.ALERT_MODAL_CANCEL_CL, {
      action: ACTION.QUIT,
      step: 1,
      link: 'finish_create_page',
    });
    setShouldShowQuitModal(false);
  };

  const dismissQuitModal = () => {
    trackEventWithData(FPTI_SPEC.ALERT_MODAL_DISMISS_CL, {
      action: ACTION.QUIT,
      step: 1,
    });
    setShouldShowQuitModal(false);
  };

  const renderQuitModal = () => {
    const fptiSpec = {
      impression: FPTI_SPEC.ALERT_MODAL_IM,
      data: {
        action: ACTION.QUIT,
        step: 1,
      },
    };
    return (
      <BasicModal
        className="modal-overlay"
        id="quit-creation-alert-modal"
        buttonLabel={quitModal.buttonLabel}
        buttonOnClick={closeQuitModal}
        closeOnBackgroundClick={true}
        header={quitModal.header}
        linkLabel={quitModal.linkLabel}
        linkOnClick={handleQuitClick}
        iconType="warning"
        isOpen={shouldShowQuitModal}
        message1={quitModal.message}
        onClose={dismissQuitModal}
        fptiSpec={fptiSpec}
      />
    );
  };

  const getErrorMsg = (field) => {
    if (actionable[field]) {
      return actionable[field];
    }
    return actionable.default;
  };

  const jumpToErrorPage = (field) => {
    const currentPath = location.pathname;
    let targetPage = 1;

    if (['buttonImageUrl', 'countryCode', 'language'].includes(field)) {
      targetPage = 1;
    } else if (['purpose', 'logoUrl', 'bannerImageUrl'].includes(field)) {
      targetPage = isButtonFlow ? 2 : 1;
    } else if (
      [
        'receiverCurrency',
        'amountValue',
        'merchantAmount1',
        'merchantAmount2',
        'merchantAmount3',
        'programNames',
      ].includes(field)
    ) {
      targetPage = isButtonFlow ? 3 : 2;
    } else if (
      ['noteLabel', 'return', 'cancelReturn', 'extraVariablesView'].includes(
        field
      )
    ) {
      targetPage = isButtonFlow ? 4 : 3;
    }

    setCurrentStep(targetPage);
    history.push(getPageUrl(targetPage, currentPath));
  };

  const handleServerSideError = (error) => {
    if (error) {
      setErrorCode(error.name);
      switch (error.name) {
        case 'VALIDATION': {
          // detail[0].field is in the format of button_variable.business
          // get the actual field name
          const errorFieldDetail = _get(error, 'details[0].field');
          const errField = errorFieldDetail
            ? errorFieldDetail.split('.').slice(-1)[0]
            : errorFieldDetail;
          const errMsg = getErrorMsg(errField);
          const clientFieldName = mapServerFieldToClientField(errField);

          let errObj = {};
          errObj[clientFieldName] = errMsg;

          setShouldShowActionableErrorModal(true);
          setErrorDesc(JSON.stringify(errorFieldDetail));
          jumpToErrorPage(clientFieldName);
          return errObj;
        }
        case 'COUNTRY_NOT_ALLOWED':
        case 'UNAVAILABLE_FOR_NON_PPCC': {
          let wizard = document.getElementById('button-factory-wizard');
          wizard.parentNode.removeChild(wizard);
          let marketingFooter =
            document.getElementsByClassName('global-footer')[0];
          if (marketingFooter) {
            marketingFooter.setAttribute(
              'style',
              'position: absolute; bottom: 0px; width: 100%; top: inherit'
            );
          }
        }
        // eslint-disable-next-line no-fallthrough
        default: {
          setErrorCode('UNACTIONABLE');
          setShouldShowUnactionableErrorModal(true);
          return;
        }
      }
    } else {
      setShouldShowUnactionableErrorModal(true);
    }
  };

  const saveButtonErrorHandler = (response, reject) => {
    const errObj = handleServerSideError(_get(response, 'responseJSON.button'));

    if (!_isEmpty(errObj)) {
      reject(new SubmissionError(errObj));
    }
    hideSpinner();
  };

  const handleWizardFormSubmit = (buttonData) => {
    trackEventWithGlobalData(finishBtnFptiSpec, {
      ...(isSetAmountPage
        ? {
            amount_type: amountType,
            choose_program: listPrograms === 'yes' ? 'on' : 'off',
            curr: receiverCurrency,
            fee_type: coverFeesEnabled === 'yes' ? 'offset' : 'none',
            is_recurring: recurringEnabled === 'yes' ? 'on' : 'off',
            step: currentStep,
          }
        : {}),
      ...(isAdvancedSettingsPage
        ? {
            addr_index: noShipping === 'no' ? 'on' : 'off',
            advanced_variables:
              noAdvancedVariables === 'no' && !_isEmpty(extraVariablesView)
                ? 'yes'
                : 'no',
            is_allow_note: noNote === 'no' ? 'yes' : 'no',
            redirect_to_url:
              noCancelReturn === 'no' &&
              (!_isEmpty(cancelReturn) || !_isEmpty(returnUrl))
                ? 'yes'
                : 'no',
            step: currentStep,
          }
        : {}),
    });

    // Clear errors on submit
    showSpinner();

    if (isDuplicating) {
      delete buttonData.id;
    } else if (isEditing) {
      buttonData.id = buttonFactoryV3.button?.id;
    }

    return new Promise((resolve, reject) => {
      saveButton(buttonData)
        .then((response) => {
          if (!response.ok) {
            saveButtonErrorHandler(response, reject);
          } else {
            const buttonId = _get(response, 'button.id');
            if (!_isEmpty(buttonId)) {
              setCurrentStep(1);
              history.push(`/donate/buttons/manage/${buttonId}`);
              const coverFees =
                getButtonVariable(
                  _get(response, 'button'),
                  'donor_covers_fees'
                ) === 'true';

              trackWizardDetailsPage({
                button_id: buttonId,
                hosted_button_id: buttonId,
                fee_payer: coverFees ? 'variable' : 'merchant',
              });
            } else {
              const receiverCountry = _get(
                response,
                'data.clientData.userData.legalCountry'
              );
              trackWizardDetailsPage({
                rcvrcntry: receiverCountry,
              });
              setCurrentStep(1);
              history.push('/donate/buttons/manage/unhosted');
            }
            // destroy form
            // We use `destroyOnUnmount: false` which causes redux-form to save values
            // after unmount. So, manually destroy the form to clear values
            destroyForm();
          }
        })
        .catch((error) => {
          saveButtonErrorHandler(error, reject);
        });
    });
  };

  const handleImageUpload = async () => {
    trackEventWithData(FPTI_SPEC.UPLOAD_BUTTON_IMAGE_SAVE_CL);
    return new Promise((resolve, reject) => {
      uploadImage(buttonImageUrl)
        .then((response) => {
          if (response.ok) {
            return resolve(response);
          }

          let error = {
            processingStage: 'IMAGE_UPLOAD_ERROR',
            response,
          };
          trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
            error_code: 'IMAGE_UPLOAD_ERROR',
            error_desc: 'button_image_url',
            error_detail: JSON.stringify(response),
            screen: SCREEN.EXPERIENCE_SETUP,
            step: currentStep,
          });
          return reject(error);
        })
        .catch((error) => {
          trackEventWithData(FPTI_SPEC.FIELD_LEVEL_ERROR_IM, {
            error_code: 'IMAGE_UPLOAD_ERROR',
            error_desc: 'button_image_url',
            error_detail: JSON.stringify(error),
            screen: SCREEN.EXPERIENCE_SETUP,
            step: currentStep,
          });
          return reject({ ...error, processingStage: 'IMAGE_UPLOAD_ERROR' });
        });
    });
  };

  // Image upload Experience
  const buttonDropzoneSubmit = async () => {
    setDropzoneError('');
    if (usePaypalButtonStyle === 'no') {
      if (!_isEmpty(buttonImageUrl)) {
        const isPayPalImage = shouldUsePayPalButtonStyle({
          button_image_url: buttonImageUrl,
        });

        if (isPayPalImage === 'yes') {
          setDropzoneError(errors.dropzone.tryAgain);
        } else if (imageUpdated && isPayPalImage === 'no') {
          try {
            setShowButtonSpinner(true);
            let response = await handleImageUpload();
            if (response.success) {
              dispatch(
                change(BTN_FACTORY_V3_FORM, 'buttonImageUrl', response.src)
              );
              setImageUpdated(false);
              handleContinueClick();
            } else {
              setDropzoneError(errors.dropzone.tryAgain);
            }
            setShowButtonSpinner(false);
          } catch (e) {
            setShowButtonSpinner(false);
            setDropzoneError(errors.dropzone.tryAgain);
          }
        } else if (_isEmpty(buttonImageUrl)) {
          setDropzoneError(errors.dropzone.tryAgain);
        } else {
          handleContinueClick();
        }
      } else {
        setDropzoneError(errors.dropzone.tryAgain);
      }
    } else {
      handleContinueClick();
    }
  };

  const handleBannerAndLogoErrorCheck = () => {
    const logoError =
      noLogoImage === 'no' &&
      ((formValues.logoTarget === LOGO_TARGET_BUTTON &&
        _isEmpty(formValues.logoUrl)) ||
        (formValues.logoTarget === LOGO_TARGET_PROFILE &&
          _isEmpty(formValues.businessProfileLogoUrl)) ||
        formValues.logoTarget === LOGO_TARGET_NOLOGO);
    const bannerError = noBannerImage === 'no' && _isEmpty(bannerImageUrl);
    setLogoClientError('');
    setBannerClientError('');
    if (logoError || bannerError) {
      if (logoError) {
        setLogoClientError(errors.logo.missingLogo);
      }
      if (bannerError) {
        setBannerClientError(errors.image.missingImage);
      }
      return false;
    }

    return true;
  };

  const handleContinueFinishButton = () => {
    if (currentStep === 1) {
      if (isDonateLink()) {
        const res = handleBannerAndLogoErrorCheck();
        if (res) {
          handleContinueClick();
        }
      } else {
        buttonDropzoneSubmit();
      }
    } else if (currentStep === 2 && isHostedFlow()) {
      const res = handleBannerAndLogoErrorCheck();
      if (res) {
        handleContinueClick();
      }
    } else {
      handleContinueClick();
    }
  };

  const renderWizardStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <WizardStep1Form
            uploadImage={uploadImage}
            handleBack={handleCancelClick}
            onSubmit={handleContinueFinishButton}
            updateBusinessProfileLogo={updateBusinessProfileLogo}
            updateBusinessImageUrls={updateBusinessImageUrls}
            setImageUpdated={setImageUpdated}
            setDropzoneError={setDropzoneError}
            dropzoneError={dropzoneError}
            logoClientError={logoClientError}
            bannerClientError={bannerClientError}
            showSpinner={showButtonSpinner}
          />
        );

      case 2:
        return (
          <WizardStep2Form
            uploadImage={uploadImage}
            updateBusinessProfileLogo={updateBusinessProfileLogo}
            updateBusinessImageUrls={updateBusinessImageUrls}
            handleBack={handleBackClick}
            logoClientError={logoClientError}
            bannerClientError={bannerClientError}
            onSubmit={
              isUnhostedFlow() && !isDonateButton()
                ? handleWizardFormSubmit
                : handleContinueFinishButton
            }
          />
        );

      case 3:
        return (
          <WizardStep3Form
            handleBack={handleBackClick}
            onSubmit={
              isHosted && isButtonFlow
                ? handleContinueClick
                : handleWizardFormSubmit
            }
          />
        );

      case 4:
        return (
          <WizardStep4Form
            handleBack={handleBackClick}
            onSubmit={handleWizardFormSubmit}
          />
        );

      default:
        return null;
    }
  };

  const renderPreview = () => {
    if (currentStep === 1 && isDonateButton()) {
      return (
        <WizardStep1Preview
          usePaypalButtonStyle={usePaypalButtonStyle === 'yes'}
          buttonSize={buttonSize}
          showAcceptedCardLogos={!!showAcceptedCardLogos}
          buttonImageUrl={buttonImageUrl}
          onError={handleButtonPreviewError}
          locale={getLanguage()}
          country={countryCode}
        />
      );
    }

    return (
      <PreviewNavigator
        className="preview-navigator"
        currentStep={currentStep}
        formValues={formValues}
        fptiSpec={previewTabFptiSpec}
        isMobile={buttonFactoryV3.isMobile}
      />
    );
  };

  const closeActionableErrorModal = () => {
    trackEventWithData(FPTI_SPEC.ACTIONABLE_ERROR_MODAL_REVIEW_CL, {
      step: currentStep,
      error_code: errorCode,
      error_desc: errorDesc,
    });
    setShouldShowActionableErrorModal(false);
    setErrorDesc(null);
    setErrorCode(null);
  };

  const dismissActionableErrorModal = () => {
    trackEventWithData(FPTI_SPEC.ACTIONABLE_ERROR_MODAL_DISMISS_CL, {
      step: currentStep,
      error_code: errorCode,
      error_desc: errorDesc,
    });
    setShouldShowActionableErrorModal(false);
    setErrorDesc(null);
    setErrorCode(null);
  };

  const closeUnactionableErrorModal = () => {
    trackEventWithData(FPTI_SPEC.ERROR_INTERACTION_DISMISS_CL, {
      step: currentStep,
      error_code: errorCode,
    });
    setShouldShowUnactionableErrorModal(false);
    setErrorCode(null);
  };

  const handleStartOverFromActionableErrorModal = () => {
    showSpinner();
    trackEventWithData(FPTI_SPEC.ACTIONABLE_ERROR_MODAL_START_OVER_CL, {
      step: currentStep,
      error_code: errorCode,
      error_desc: errorDesc,
    });
    const buttonType = getParameterByName('type');
    window.location.href = !_isEmpty(buttonType)
      ? `/donate/buttons?type=${buttonType}`
      : '/donate/buttons';
  };

  const handleStartOverFromUnactionableErrorModal = () => {
    showSpinner();
    trackEventWithData(FPTI_SPEC.ERROR_INTERACTION_LINK_CL, {
      step: currentStep,
      error_code: errorCode,
      link: 'start_over',
      screen,
    });
    const buttonType = getParameterByName('type');
    window.location.href = !_isEmpty(buttonType)
      ? `/donate/buttons?type=${buttonType}`
      : '/donate/buttons';
  };

  const handleRetry = () => {
    trackEventWithData(FPTI_SPEC.ERROR_INTERACTION_LINK_CL, {
      step: currentStep,
      error_code: errorCode,
      link: 'try_again',
      screen,
    });
    dispatch(submit(BTN_FACTORY_V3_FORM));
  };

  const renderActionableErrorModal = () => {
    const fptiSpec = {
      impression: FPTI_SPEC.ACTIONABLE_ERROR_MODAL_IM,
      data: {
        step: currentStep,
        error_code: errorCode,
        error_desc: errorDesc,
      },
    };

    return (
      <BasicModal
        className="modal-overlay"
        id="actionable-error-modal-v3"
        buttonLabel={actionable.review}
        buttonOnClick={closeActionableErrorModal}
        closeOnBackgroundClick={true}
        header={actionable.header}
        linkLabel={actionable.cancel}
        linkOnClick={handleStartOverFromActionableErrorModal}
        iconType="critical"
        isOpen={shouldShowActionableErrorModal}
        message1={actionable.msg1}
        message2={actionable.msg2}
        onClose={dismissActionableErrorModal}
        fptiSpec={fptiSpec}
      />
    );
  };

  const renderUnactionableErrorModal = () => {
    const fptiSpec = {
      impression: FPTI_SPEC.ERROR_INTERACTION_IM,
      data: {
        step: currentStep,
        error_code: errorCode,
        flow: isButtonFlow ? 'button' : 'link',
        screen,
      },
    };

    return (
      <BasicModal
        className="modal-overlay"
        id="unactionable-error-modal-v3"
        buttonLabel={unactionable.tryagain}
        buttonOnClick={handleRetry}
        closeOnBackgroundClick={true}
        header={unactionable.header}
        linkLabel={unactionable.startover}
        linkOnClick={handleStartOverFromUnactionableErrorModal}
        iconType="warning"
        isOpen={shouldShowUnactionableErrorModal}
        message1={unactionable.msg}
        onClose={closeUnactionableErrorModal}
        fptiSpec={fptiSpec}
      />
    );
  };

  const stepsString = stepCount
    .replace(new RegExp('{currentStep}'), currentStep)
    .replace(new RegExp('{totalSteps}'), totalSteps);

  const duplicateToast = (
    <Toast
      id="duplicate-donation-page-toast"
      message={duplicatePage}
      isSuccess={true}
    />
  );

  if (!isLoggedInUser()) {
    return (
      <ButtonFactoryLogin
        handleLogin={handleLogin}
        handleSignUp={handleSignUp}
      />
    );
  }

  if (isCountryNotSupported) {
    return <CountryNotSupportedTile />;
  }

  const previewHeader = isButtonConfigurePage
    ? previewContainerHeader
    : previewTitle;

  const previewButtonLabel = isButtonConfigurePage
    ? previewButton
    : previewDonatePage;

  return (
    <>
      {shouldShowDuplicateToast && duplicateToast}
      <Container
        className="button-factory-wizard-v3"
        id="button-factory-wizard-v3"
      >
        <ExtendSession
          isLoggedIn={isLoggedInUser()}
          currentStep={currentStep}
        />
        <Row className="topLinks">
          {!buttonFactoryV3.isMobile ? (
            <>
              <div className="goBack">
                {isHostedFlow() && (
                  <Button
                    className="button-link"
                    type="button"
                    onClick={handleTopCancelClick}
                    iconComponent={ArrowLeftIcon}
                    tertiary
                  >
                    {backFundraisers}
                  </Button>
                )}
              </div>
              <DonateButtonFeedback
                currentStep={currentStep}
                fptiSpec={feedbackBtnFptiSpec}
              />
            </>
          ) : (
            <>
              <IconButton
                buttonType="tertiary"
                svgIconComponent={ArrowLeftIcon}
                onClick={
                  currentStep === 1 ? handleTopCancelClick : handleBackClick
                }
              ></IconButton>
              <DonateButtonFeedback
                currentStep={currentStep}
                fptiSpec={feedbackBtnFptiSpec}
              />
            </>
          )}
        </Row>

        <Row className="button-factory-wizard-v3-content">
          <Col className="col-wizard-step" lg={4}>
            <ProgressMeter
              className="progress-meter-bf3"
              max={totalSteps}
              value={currentStep}
              steps={stepsString}
              title={progressMeterTitles[currentStep]}
            />
            {!busy && renderWizardStep()}
          </Col>

          {!buttonFactoryV3.isMobile ? (
            <Col className="col-button-preview" lg={8}>
              {renderPreview()}
            </Col>
          ) : null}
        </Row>
        {buttonFactoryV3.isMobile ? (
          <WizardMobilePreview
            handleSubmit={() => dispatch(submit(BTN_FACTORY_V3_FORM))}
            openMobilePreview={openMobilePreview}
            isMobilePreviewOpen={buttonFactoryV3.isMobilePreviewOpen}
            closeMobilePreview={closeMobilePreview}
            steps={{ currentStep, totalSteps }}
            labels={{
              continueButton,
              finishButton,
              previewButton: previewButtonLabel,
              previewTitle: previewHeader,
            }}
            usePaypalButtonStyle={usePaypalButtonStyle === 'yes'}
            buttonSize={buttonSize}
            showAcceptedCardLogos={!!showAcceptedCardLogos}
            buttonImageUrl={buttonImageUrl}
            onError={handleButtonPreviewError}
            locale={getLanguage()}
            country={countryCode}
            showSpinner={showButtonSpinner}
            buttonDropzoneSubmit={buttonDropzoneSubmit}
            handleBannerAndLogoErrorCheck={handleBannerAndLogoErrorCheck}
          />
        ) : null}
      </Container>

      {renderActionableErrorModal()}
      {renderUnactionableErrorModal()}
      {renderQuitModal()}
    </>
  );
};

ButtonFactoryWizardV3.propTypes = {
  application: PropTypes.object,
  buttonFactoryV3: PropTypes.object,
  destroyForm: PropTypes.func,
  dispatch: PropTypes.func,
  form: PropTypes.object,
  getButton: PropTypes.func,
  goToNextStep: PropTypes.func,
  goToPreviousStep: PropTypes.func,
  hideActionableErroModal: PropTypes.func,
  hideUnactionableErrorModal: PropTypes.func,
  history: PropTypes.object,
  initializeWizard: PropTypes.func,
  location: PropTypes.object,
  match: PropTypes.object,
  saveButton: PropTypes.func,
  uploadImage: PropTypes.func,
  updateBusinessProfileLogo: PropTypes.func,
  updateBusinessImageUrls: PropTypes.func,
  updateFlowSpec: PropTypes.func,
  setCurrentStep: PropTypes.func,
  showActionableErrorModal: PropTypes.func,
  showUnactionableErrorModal: PropTypes.func,
  openMobilePreview: PropTypes.func,
  closeMobilePreview: PropTypes.func,
  updateIsMobile: PropTypes.func,
  optionsForLanguage: PropTypes.array,
};

const mapStateToProps = (state) => {
  const { application = {}, buttonFactoryV3 = {}, form = {} } = state;
  const formValues = form.buttonFactoryV3?.values;
  const countryAndLanguageDetails = _get(
    state,
    'application.countryAndLanguageDetails'
  );
  const countryCode =
    _get(formValues, 'countryCode') ||
    _get(state.buttonFactory, 'formattedButton.countryCode');
  return {
    application,
    buttonFactoryV3,
    form,
    optionsForLanguage: getOptionsForLanguageField(
      countryAndLanguageDetails,
      countryCode
    ),
  };
};

const mapDispatchToProps = (dispatch) => {
  return { ...bindActionCreators(ButtonFactoryActions, dispatch), dispatch };
};

const ButtonFactoryWizardFormV3 = connect(
  mapStateToProps,
  mapDispatchToProps
)(ButtonFactoryWizardV3);
export default withRouter(ButtonFactoryWizardFormV3);
